/**
 * ClampedHtml — renders pre-sanitized HTML clamped to a max height, with a
 * "Show more / Show less" toggle when the content overflows. Used so long
 * status narratives don't auto-expand the card (DEV-1296).
 *
 * The caller MUST sanitize the HTML (e.g. via sanitizeRichText) before passing.
 */

import { useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './ClampedHtml.module.css';

interface Props {
  /** Pre-sanitized HTML. */
  html: string;
  /** Class for the content block (typography). */
  className?: string;
  /** Collapsed max height in px. */
  collapsedMaxHeight?: number;
}

export default function ClampedHtml({ html, className, collapsedMaxHeight = 220 }: Props) {
  const { t } = useAppTranslation('common');
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > collapsedMaxHeight + 4);
  }, [html, collapsedMaxHeight]);

  const collapsed = overflowing && !expanded;

  return (
    <div>
      <div
        ref={ref}
        className={`${className ?? ''} ${collapsed ? styles.collapsed : ''}`.trim()}
        style={collapsed ? { maxHeight: collapsedMaxHeight } : undefined}
        // Caller sanitizes (sanitizeRichText) before passing.
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {overflowing && (
        <button type="button" className={styles.moreBtn} onClick={() => setExpanded((v) => !v)}>
          {expanded ? t('showLess') : t('showMore')}
        </button>
      )}
    </div>
  );
}
